import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useUserContext } from '../context/UserContext';
import { useStoresQuery } from '../hooks/useStoresQuery';
import { useSuppliers } from '../hooks/useSuppliers';
import { useMediaUpload } from '../hooks/useMediaUpload';
import { useSubmitComplaint } from '../hooks/useSubmitComplaint';
import { ROUTES } from '../routes';
import {
  Upload,
  X,
  AlertCircle,
  Camera,
  FileText,
  Store,
  Building2,
  Package,
  CheckCircle,
  Loader2,
  Check,
  Factory,
  CalendarDays,
  Hash,
  Boxes,
} from 'lucide-react';
import { Autocomplete, StructuredPhotoSlot } from '../components/ui';
import type { Store as StoreType, ComplaintCategory } from '../api';
import { getPresignedUploadUrl } from '../api';
import { useProducts } from '../hooks/useProducts';
import { useManufacturers } from '../hooks/useManufacturers';
import { useCategories } from '../hooks/useCategories';
import { computeFileChecksum } from '../utils/fileChecksum';

interface FormData {
  storeId: string;
  supplierId: string;
  category: string;
  productId: string;
  productName: string;
  productCode: string;
  description: string;
  manufacturer: string;
  deliveryDate: string;
  batchCode: string;
  affectedUnits: string;
  affectedUnitType: 'BAG' | 'CASE';
}

const steps = [
  { number: 1, label: 'Select', shortLabel: 'Select' },
  { number: 2, label: 'Details', shortLabel: 'Details' },
  { number: 3, label: 'Evidence', shortLabel: 'Evidence' },
];

export function SubmitComplaint() {
  const navigate = useNavigate();
  const { user, brandId } = useUserContext();
  const [formData, setFormData] = useState<FormData>({
    storeId: '',
    supplierId: '',
    category: '',
    productId: '',
    productName: '',
    productCode: '',
    description: '',
    manufacturer: '',
    deliveryDate: new Date().toISOString().split('T')[0],
    batchCode: '',
    affectedUnits: '',
    affectedUnitType: 'BAG',
  });

  const { data: storesData, isLoading: isLoadingStores } = useStoresQuery(!!user);
  const { data: suppliersData, isLoading: isLoadingSuppliers } = useSuppliers(!!user);
  const { data: productsData } = useProducts(
    formData.supplierId || undefined,
    !!user && !!brandId
  );
  const { data: manufacturersData, isLoading: isLoadingManufacturers } = useManufacturers(
    formData.supplierId || undefined,
    !!user && !!brandId
  );
  const manufacturers = manufacturersData?.data || [];
  const { data: categoriesData, isLoading: isLoadingCategories } = useCategories(!!user && !!brandId, brandId);
  const stores = storesData?.data || [];
  const suppliers = suppliersData?.data || [];

  // Categories from API only
  const categories = useMemo(() => {
    const apiCats = categoriesData ?? [];
    return apiCats.map((c) => ({
      id: c.categoryId as ComplaintCategory,
      label: c.label,
      description: c.description || '',
    }));
  }, [categoriesData]);

  // Media upload hook
  const {
    uploads,
    isUploading,
    uploadFiles,
    removeUpload,
    removeFailedUploads,
    completedMediaKeys,
    completedMediaChecksums,
    knownChecksums,
    maxFiles,
  } = useMediaUpload();

  // Structured photo uploads (bag and case pictures) — track checksums to
  // dedup against each other and the additional-uploads list.
  const [bagPicture, setBagPicture] = useState<{ mediaKey: string; previewUrl: string; checksum?: string } | null>(null);
  const [bagPictureUploading, setBagPictureUploading] = useState(false);
  const [bagPictureProgress, setBagPictureProgress] = useState(0);
  const [casePicture, setCasePicture] = useState<{ mediaKey: string; previewUrl: string; checksum?: string } | null>(null);
  const [casePictureUploading, setCasePictureUploading] = useState(false);
  const [casePictureProgress, setCasePictureProgress] = useState(0);
  const [showAdditionalEvidence, setShowAdditionalEvidence] = useState(false);

  // Submit complaint mutation
  const submitMutation = useSubmitComplaint();

  const [errors, setErrors] = useState<Partial<Record<keyof FormData | 'submit' | 'bagPicture' | 'casePicture', string>>>({});
  const [currentStep, setCurrentStep] = useState(1);


  // Track when user explicitly clicks submit to prevent accidental form submissions
  const intentionalSubmitRef = useRef(false);
  // Track step change time to prevent submission during step transition
  const lastStepChangeRef = useRef<number>(0);

  const totalSteps = 3;

  // Memoized selected store and supplier to avoid duplicate lookups
  const selectedStore = useMemo(
    () => stores.find((s) => s.internalStoreNumber === formData.storeId),
    [stores, formData.storeId]
  );
  const selectedSupplier = useMemo(
    () => suppliers.find((s) => s.vendorId === formData.supplierId),
    [suppliers, formData.supplierId]
  );

  // Check for failed uploads
  const hasFailedUploads = useMemo(
    () => uploads.some((u) => u.status === 'error'),
    [uploads]
  );

  // Clear "Maximum N files" error when uploads are removed and count is back under limit
  useEffect(() => {
    const nonErrorCount = uploads.filter((u) => u.status !== 'error').length;
    if (nonErrorCount <= (maxFiles ?? 5)) {
      setErrors((prev) => {
        if (prev.submit?.includes('Maximum') && prev.submit?.includes('files')) {
          return { ...prev, submit: undefined };
        }
        return prev;
      });
    }
  }, [uploads, maxFiles]);

  const catalogProducts = useMemo(() => productsData?.data ?? [], [productsData?.data]);

  const productOptions = useMemo(
    () =>
      catalogProducts.map((p) => ({
        value: p.productId,
        label: p.name,
        secondary: [p.productCode, p.category].filter(Boolean).join(' · ') || undefined,
      })),
    [catalogProducts]
  );

  // Warn user if navigating away with in-progress uploads
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isUploading) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isUploading]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name as keyof FormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  // Build the union of "already-attached" checksums on this submission so
  // any of the three upload sites (bag, case, additional) can dedup against
  // the other two.
  const submissionKnownChecksums = useMemo(() => {
    const list = [...knownChecksums];
    if (bagPicture?.checksum) list.push({ checksum: bagPicture.checksum, fileName: 'Bag Picture' });
    if (casePicture?.checksum) list.push({ checksum: casePicture.checksum, fileName: 'Case Picture' });
    return list;
  }, [knownChecksums, bagPicture, casePicture]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(e.target.files || []);
    if (newFiles.length > 0) {
      try {
        // Pass bag/case checksums so additional uploads dedup against them
        await uploadFiles(newFiles, undefined, [
          ...(bagPicture?.checksum ? [{ checksum: bagPicture.checksum, fileName: 'Bag Picture' }] : []),
          ...(casePicture?.checksum ? [{ checksum: casePicture.checksum, fileName: 'Case Picture' }] : []),
        ]);
      } catch (error) {
        setErrors((prev) => ({
          ...prev,
          submit: error instanceof Error ? error.message : 'Failed to upload files',
        }));
      }
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const handleStructuredPhotoUpload = async (
    file: File,
    setter: typeof setBagPicture,
    setUploading: typeof setBagPictureUploading,
    setProgress: typeof setBagPictureProgress,
    slotLabel: 'Bag Picture' | 'Case Picture',
    errorKey: 'bagPicture' | 'casePicture',
  ) => {
    setUploading(true);
    setProgress(0);
    setErrors((prev) => ({ ...prev, [errorKey]: undefined }));
    try {
      // Compute checksum and reject if it duplicates any other attachment
      // already on the submission (the other slot or any additional upload).
      const checksum = await computeFileChecksum(file);
      if (checksum) {
        const conflict = submissionKnownChecksums.find((k) => k.checksum === checksum);
        if (conflict) {
          setErrors((prev) => ({
            ...prev,
            [errorKey]: `Duplicate of "${conflict.fileName}". Please choose a different photo.`,
          }));
          return;
        }
      }

      const { data } = await getPresignedUploadUrl({ contentType: file.type, fileName: file.name });
      // Upload to S3 with progress tracking via XMLHttpRequest
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error('Upload failed')));
        xhr.onerror = () => reject(new Error('Upload failed'));
        xhr.open('PUT', data.uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });
      const previewUrl = URL.createObjectURL(file);
      setter({ mediaKey: data.mediaKey, previewUrl, checksum: checksum || undefined });
      void slotLabel; // referenced for type-checker / future log labelling
    } catch (err) {
      setErrors((prev) => ({ ...prev, submit: err instanceof Error ? err.message : 'Photo upload failed' }));
    } finally {
      setUploading(false);
    }
  };

  const validateStep = (step: number, accumulatedErrors: Partial<Record<keyof FormData | 'submit' | 'bagPicture' | 'casePicture', string>> = {}): boolean => {
    const newErrors: Partial<Record<keyof FormData | 'submit' | 'bagPicture' | 'casePicture', string>> = { ...accumulatedErrors };

    if (step === 1) {
      if (!formData.storeId) newErrors.storeId = 'Please select a store';
      if (!formData.supplierId) newErrors.supplierId = 'Please select a distributor';
      if (!formData.manufacturer.trim()) newErrors.manufacturer = 'Manufacturer is required';
      if (formData.manufacturer.trim().length > 200) newErrors.manufacturer = 'Manufacturer name is too long (max 200 characters)';
      if (!formData.category) newErrors.category = 'Please select a category';
    }

    if (step === 2) {
      if (catalogProducts.length > 0 && !formData.productId) {
        newErrors.productName = 'Please select a product from the catalog';
      } else if (!formData.productName) {
        newErrors.productName = 'Product name is required';
      }
      if (!formData.description) newErrors.description = 'Please describe the issue';
      if (formData.description && formData.description.length < 10) {
        newErrors.description = 'Description must be at least 10 characters';
      }
      if (formData.description && formData.description.length > 5000) {
        newErrors.description = 'Description is too long (max 5000 characters)';
      }
      // Validate delivery date
      if (!formData.deliveryDate) {
        newErrors.deliveryDate = 'Delivery date is required';
      } else {
        const dd = new Date(formData.deliveryDate);
        if (isNaN(dd.getTime())) {
          newErrors.deliveryDate = 'Please enter a valid date';
        } else if (dd > new Date()) {
          newErrors.deliveryDate = 'Delivery date cannot be in the future';
        }
      }
      // Validate batch code
      if (!formData.batchCode.trim()) {
        newErrors.batchCode = 'Batch code is required';
      } else if (formData.batchCode.trim().length > 100) {
        newErrors.batchCode = 'Batch code is too long (max 100 characters)';
      }
      // Validate affected units
      if (!formData.affectedUnits) {
        newErrors.affectedUnits = 'Number of affected units is required';
      } else {
        const units = parseInt(formData.affectedUnits, 10);
        if (isNaN(units) || units < 1) {
          newErrors.affectedUnits = 'Must be at least 1';
        } else if (units > 99999) {
          newErrors.affectedUnits = 'Maximum 99,999 units';
        }
      }
    }

    if (step === 3) {
      if (!bagPicture) newErrors.bagPicture = 'Bag picture is required';
      if (!casePicture) newErrors.casePicture = 'Case picture is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validate all steps up to and including the current step
  const validateAllSteps = (): boolean => {
    const allErrors: Partial<Record<keyof FormData | 'submit' | 'bagPicture' | 'casePicture', string>> = {};

    // Validate step 1
    if (!formData.storeId) allErrors.storeId = 'Please select a store';
    if (!formData.supplierId) allErrors.supplierId = 'Please select a distributor';
    if (!formData.manufacturer.trim()) allErrors.manufacturer = 'Manufacturer is required';
    if (formData.manufacturer.trim().length > 200) allErrors.manufacturer = 'Manufacturer name is too long (max 200 characters)';
    if (!formData.category) allErrors.category = 'Please select a category';

    // Validate step 2
    if (catalogProducts.length > 0 && !formData.productId) {
      allErrors.productName = 'Please select a product from the catalog';
    } else if (!formData.productName) {
      allErrors.productName = 'Product name is required';
    }
    if (!formData.description) allErrors.description = 'Please describe the issue';
    if (formData.description && formData.description.length < 10) {
      allErrors.description = 'Description must be at least 10 characters';
    }
    if (formData.description && formData.description.length > 5000) {
      allErrors.description = 'Description is too long (max 5000 characters)';
    }
    if (!formData.deliveryDate) {
      allErrors.deliveryDate = 'Delivery date is required';
    } else {
      const dd = new Date(formData.deliveryDate);
      if (isNaN(dd.getTime())) allErrors.deliveryDate = 'Please enter a valid date';
      else if (dd > new Date()) allErrors.deliveryDate = 'Delivery date cannot be in the future';
    }
    if (!formData.batchCode.trim()) allErrors.batchCode = 'Batch code is required';
    else if (formData.batchCode.trim().length > 100) allErrors.batchCode = 'Batch code is too long (max 100 characters)';
    if (!formData.affectedUnits) {
      allErrors.affectedUnits = 'Number of affected units is required';
    } else {
      const units = parseInt(formData.affectedUnits, 10);
      if (isNaN(units) || units < 1) allErrors.affectedUnits = 'Must be at least 1';
      else if (units > 99999) allErrors.affectedUnits = 'Maximum 99,999 units';
    }
    // Validate structured photos
    if (!bagPicture) allErrors.bagPicture = 'Bag picture is required';
    if (!casePicture) allErrors.casePicture = 'Case picture is required';

    setErrors(allErrors);
    return Object.keys(allErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      // Track step change time to prevent accidental form submission during transition
      lastStepChangeRef.current = Date.now();
      setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Only allow submission from the final step (Evidence)
    // This prevents accidental form submission from earlier steps
    if (currentStep !== totalSteps) {
      return;
    }

    // Prevent submission if it wasn't triggered by clicking the submit button
    if (!intentionalSubmitRef.current) {
      return;
    }
    // Reset the flag
    intentionalSubmitRef.current = false;

    // Prevent submission within 500ms of step change to avoid race conditions
    const timeSinceStepChange = Date.now() - lastStepChangeRef.current;
    if (timeSinceStepChange < 500) {
      return;
    }

    // Validate all steps, not just the current one
    if (!validateAllSteps()) return;

    // Clear any previous submit error
    setErrors((prev) => ({ ...prev, submit: undefined }));

    // Verify store and supplier still exist (could have been removed from list)
    if (!selectedStore || !selectedSupplier) {
      setErrors((prev) => ({
        ...prev,
        submit: 'Invalid store or distributor selection. Please go back and re-select.',
      }));
      return;
    }

    // Warn about failed uploads
    if (hasFailedUploads) {
      setErrors((prev) => ({
        ...prev,
        submit: 'Some files failed to upload. Please remove them or try again before submitting.',
      }));
      return;
    }

    // Validate brand context exists
    const effectiveBrandId = brandId || selectedStore.brand;
    if (!effectiveBrandId) {
      setErrors((prev) => ({
        ...prev,
        submit: 'Brand context is missing. Please refresh and try again.',
      }));
      return;
    }

    try {
      // Build mediaChecksums map covering bag, case, and additional uploads
      const mediaChecksums: Record<string, string> = { ...completedMediaChecksums };
      if (bagPicture?.mediaKey && bagPicture.checksum) {
        mediaChecksums[bagPicture.mediaKey] = bagPicture.checksum;
      }
      if (casePicture?.mediaKey && casePicture.checksum) {
        mediaChecksums[casePicture.mediaKey] = casePicture.checksum;
      }

      await submitMutation.mutateAsync({
        storeId: formData.storeId,
        storeName: selectedStore.storeName,
        brandId: effectiveBrandId,
        supplierId: formData.supplierId,
        supplierName: selectedSupplier.name,
        category: formData.category as ComplaintCategory,
        productId: formData.productId || undefined,
        productName: formData.productName,
        productCode: formData.productCode || undefined,
        description: formData.description,
        manufacturerId: manufacturers.some(m => m.manufacturerId === formData.manufacturer)
          ? formData.manufacturer
          : undefined,
        manufacturer: (manufacturers.find(m => m.manufacturerId === formData.manufacturer)?.name || formData.manufacturer).trim(),
        deliveryDate: new Date(formData.deliveryDate).getTime(),
        batchCode: formData.batchCode.trim(),
        affectedUnits: parseInt(formData.affectedUnits, 10),
        affectedUnitType: formData.affectedUnitType,
        bagPictureUrl: bagPicture?.mediaKey,
        casePictureUrl: casePicture?.mediaKey,
        mediaUrls: completedMediaKeys,
        ...(Object.keys(mediaChecksums).length > 0 && { mediaChecksums }),
      });

      // Navigate to complaints list after successful submission
      navigate(ROUTES.COMPLAINTS);
    } catch (error) {
      submitMutation.reset();
      setErrors((prev) => ({
        ...prev,
        submit: error instanceof Error ? error.message : 'Failed to submit complaint',
      }));
    }
  };

  const isSubmitting = submitMutation.isPending;

  return (
    <div className="max-w-2xl mx-auto pb-24 lg:pb-8">
      {/* Progress Steps - Compact horizontal stepper */}
      <div className="mb-6">
        <div className="flex items-center">
          {steps.map((step, index) => (
            <div key={step.number} className="flex items-center flex-1 last:flex-none">
              {/* Step circle */}
              <div className="flex items-center gap-2">
                <div
                  data-testid={`submit-step-${step.number}`}
                  aria-current={step.number === currentStep ? 'step' : undefined}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                    step.number < currentStep
                      ? 'bg-green-500 text-white'
                      : step.number === currentStep
                        ? 'bg-gray-900 text-white ring-4 ring-gray-100'
                        : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  {step.number < currentStep ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    step.number
                  )}
                </div>
                <span
                  className={`text-sm font-medium hidden sm:block ${
                    step.number === currentStep ? 'text-gray-900' : 'text-gray-500'
                  }`}
                >
                  {step.label}
                </span>
              </div>
              {/* Connector line */}
              {index < steps.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-3 rounded ${
                    step.number < currentStep ? 'bg-green-500' : 'bg-gray-200'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
        {/* Mobile step label */}
        <p className="sm:hidden text-sm font-medium text-gray-900 mt-3">
          Step {currentStep}: {steps[currentStep - 1].label}
        </p>
      </div>

      {/* Submit error */}
      {errors.submit && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">Submission Failed</p>
            <p className="text-sm text-red-600">{errors.submit}</p>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit}>
        {/* Step 1: Basic Information */}
        {currentStep === 1 && (
          <div className="space-y-5">
            {/* Restaurant selection */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Store className="w-4 h-4 inline mr-1.5 text-gray-400" />
                Restaurant <span className="text-red-500">*</span>
              </label>
              <Autocomplete
                options={stores.map((store: StoreType) => ({
                  value: store.internalStoreNumber,
                  label: store.champsNumber
                    ? `${store.storeName} (#${store.champsNumber})`
                    : `${store.storeName} (#${store.internalStoreNumber})`,
                  secondary: store.champsNumber ? `AIS #${store.internalStoreNumber}` : undefined,
                }))}
                value={formData.storeId}
                onChange={(v) => {
                  setFormData((prev) => ({ ...prev, storeId: v }));
                  if (errors.storeId) setErrors((prev) => ({ ...prev, storeId: undefined }));
                }}
                placeholder="Search or select a store..."
                disabled={isLoadingStores}
                loading={isLoadingStores}
                error={errors.storeId}
                aria-describedby={errors.storeId ? 'storeId-error' : undefined}
                data-testid="submit-select-store"
                id="submit-store"
              />
            </div>

            {/* Distributor selection */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Building2 className="w-4 h-4 inline mr-1.5 text-gray-400" />
                Distributor <span className="text-red-500">*</span>
              </label>
              <Autocomplete
                options={suppliers.map((s) => ({ value: s.vendorId, label: s.name }))}
                value={formData.supplierId}
                onChange={(v) => {
                  setFormData((prev) => ({
                    ...prev,
                    supplierId: v,
                    manufacturer: '',
                    productId: '',
                    productName: '',
                    productCode: '',
                  }));
                  if (errors.supplierId) setErrors((prev) => ({ ...prev, supplierId: undefined }));
                  if (errors.manufacturer) setErrors((prev) => ({ ...prev, manufacturer: undefined }));
                }}
                placeholder="Search or select a distributor..."
                disabled={isLoadingSuppliers}
                loading={isLoadingSuppliers}
                error={errors.supplierId}
                aria-describedby={errors.supplierId ? 'supplierId-error' : undefined}
                data-testid="submit-select-supplier"
                id="submit-supplier"
              />
              {!isLoadingSuppliers && suppliers.length === 0 && (
                <p className="mt-1.5 text-xs text-amber-600 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  No distributors available
                </p>
              )}
            </div>

            {/* Manufacturer */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Factory className="w-4 h-4 inline mr-1.5 text-gray-400" />
                Manufacturer <span className="text-red-500">*</span>
              </label>
              <Autocomplete
                options={manufacturers.map((m) => ({ value: m.manufacturerId, label: m.name }))}
                value={formData.manufacturer}
                onChange={(v) => {
                  setFormData((prev) => ({
                    ...prev,
                    manufacturer: v,
                  }));
                  if (errors.manufacturer) setErrors((prev) => ({ ...prev, manufacturer: undefined }));
                }}
                placeholder="Search or select a manufacturer..."
                disabled={isLoadingManufacturers}
                loading={isLoadingManufacturers}
                error={errors.manufacturer}
                data-testid="submit-select-manufacturer"
                id="submit-manufacturer"
              />

            </div>

            {/* Category selection */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Package className="w-4 h-4 inline mr-1.5 text-gray-400" />
                Issue Category <span className="text-red-500">*</span>
              </label>
              <Autocomplete
                options={categories.map((cat) => ({
                  value: cat.id,
                  label: cat.label,
                  secondary: cat.description,
                }))}
                value={formData.category}
                onChange={(v) => {
                  setFormData((prev) => ({ ...prev, category: v }));
                  if (errors.category) setErrors((prev) => ({ ...prev, category: undefined }));
                }}
                placeholder="Search or select a category..."
                disabled={isLoadingCategories}
                loading={isLoadingCategories}
                error={errors.category}
                data-testid="submit-select-category"
                id="submit-category"
              />
              {!isLoadingCategories && categories.length === 0 && (
                <p className="mt-1.5 text-xs text-amber-600 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  No categories available
                </p>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Details */}
        {currentStep === 2 && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
            <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <FileText className="w-5 h-5 text-gray-600" />
              Issue Details
            </h2>

            {/* Product name — select from catalog only (no free text per Taco Bell MVP) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Product Name <span className="text-red-500">*</span>
              </label>
              <Autocomplete
                options={productOptions}
                value={formData.productId}
                onChange={(v) => {
                  const id = (v as string) || '';
                  const prod = catalogProducts.find((p) => p.productId === id);
                  setFormData((prev) => ({
                    ...prev,
                    productId: id,
                    productName: prod?.name ?? '',
                    // Pre-fill the catalog code; the store can still override it below
                    productCode: prod?.productCode ?? prev.productCode,
                  }));
                  if (errors.productName) setErrors((prev) => ({ ...prev, productName: undefined }));
                }}
                placeholder="Search or select a product..."
                filterable
                error={errors.productName}
                hint={productOptions.length === 0 ? 'No products in catalog. Add products in Admin → Product Catalog.' : undefined}
                aria-describedby={errors.productName ? 'productName-error' : undefined}
                data-testid="submit-input-product-name"
                id="submit-product-name"
              />
            </div>

            {/* Product code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Product Code / SKU <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                type="text"
                name="productCode"
                value={formData.productCode}
                onChange={handleInputChange}
                placeholder="e.g., SKU-12345"
                data-testid="submit-input-product-code"
                className="w-full px-3 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 text-sm"
              />
            </div>

            {/* Issue description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Issue Description <span className="text-red-500">*</span>
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={4}
                placeholder="Describe the quality issue in detail..."
                data-testid="submit-input-description"
                aria-invalid={!!errors.description}
                aria-describedby={errors.description ? 'description-error' : undefined}
                className={`w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 resize-none text-sm ${
                  errors.description ? 'border-red-300' : 'border-gray-200'
                }`}
              />
              <div className="flex justify-between mt-1">
                {errors.description ? (
                  <p id="description-error" className="text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.description}
                  </p>
                ) : (
                  <span />
                )}
                <p
                  className={`text-xs ${formData.description.length >= 10 ? 'text-green-600' : 'text-red-600'}`}
                  aria-live="polite"
                  aria-label={`${formData.description.length} of 10 minimum characters`}
                >
                  {formData.description.length}/10 minimum
                </p>
              </div>
            </div>

            {/* Delivery Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <CalendarDays className="w-4 h-4 inline mr-1 text-gray-400" />
                Delivery Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="deliveryDate"
                value={formData.deliveryDate}
                onChange={handleInputChange}
                max={new Date().toISOString().split('T')[0]}
                data-testid="submit-input-delivery-date"
                className={`w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 text-sm ${
                  errors.deliveryDate ? 'border-red-300' : 'border-gray-200'
                }`}
              />
              {errors.deliveryDate && (
                <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.deliveryDate}
                </p>
              )}
            </div>

            {/* Batch Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Hash className="w-4 h-4 inline mr-1 text-gray-400" />
                Batch Code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="batchCode"
                value={formData.batchCode}
                onChange={handleInputChange}
                placeholder="e.g., LOT-2024-0831"
                data-testid="submit-input-batch-code"
                className={`w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 text-sm ${
                  errors.batchCode ? 'border-red-300' : 'border-gray-200'
                }`}
              />
              {errors.batchCode && (
                <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.batchCode}
                </p>
              )}
            </div>

            {/* Affected Units */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Boxes className="w-4 h-4 inline mr-1 text-gray-400" />
                Number of Affected Units <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  name="affectedUnits"
                  value={formData.affectedUnits}
                  onChange={handleInputChange}
                  placeholder="1"
                  min={1}
                  max={99999}
                  step={1}
                  data-testid="submit-input-affected-units"
                  className={`flex-1 px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 text-sm ${
                    errors.affectedUnits ? 'border-red-300' : 'border-gray-200'
                  }`}
                />
                <select
                  value={formData.affectedUnitType}
                  onChange={(e) => setFormData((prev) => ({ ...prev, affectedUnitType: e.target.value as 'BAG' | 'CASE' }))}
                  data-testid="submit-select-unit-type"
                  className="px-3 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 text-sm bg-white min-w-[110px]"
                >
                  <option value="BAG">Bag(s)</option>
                  <option value="CASE">Case(s)</option>
                </select>
              </div>
              {errors.affectedUnits && (
                <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.affectedUnits}
                </p>
              )}
            </div>

          </div>
        )}

        {/* Step 3: Evidence */}
        {currentStep === 3 && (
          <div className="space-y-4">
            {/* Required Photos */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2 mb-1">
                <Camera className="w-5 h-5 text-gray-600" />
                Upload Evidence
              </h2>
              <p className="text-xs text-gray-500 mb-4">
                Required photos for investigation
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <StructuredPhotoSlot
                  label="Bag Picture"
                  helperText="Photo of the product bag/inner packaging"
                  required
                  value={bagPicture?.mediaKey}
                  previewUrl={bagPicture?.previewUrl}
                  isUploading={bagPictureUploading}
                  progress={bagPictureProgress}
                  error={errors.bagPicture}
                  onSelectFile={(file) => handleStructuredPhotoUpload(file, setBagPicture, setBagPictureUploading, setBagPictureProgress, 'Bag Picture', 'bagPicture')}
                  onRemove={() => setBagPicture(null)}
                  testId="submit-photo-bag"
                />
                <StructuredPhotoSlot
                  label="Case Picture"
                  helperText="Photo of the outer case/box packaging"
                  required
                  value={casePicture?.mediaKey}
                  previewUrl={casePicture?.previewUrl}
                  isUploading={casePictureUploading}
                  progress={casePictureProgress}
                  error={errors.casePicture}
                  onSelectFile={(file) => handleStructuredPhotoUpload(file, setCasePicture, setCasePictureUploading, setCasePictureProgress, 'Case Picture', 'casePicture')}
                  onRemove={() => setCasePicture(null)}
                  testId="submit-photo-case"
                />
              </div>
            </div>

            {/* Additional evidence (collapsible) */}
            <div className="border border-gray-200 rounded-lg">
              <button
                type="button"
                onClick={() => setShowAdditionalEvidence((prev) => !prev)}
                className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <span>Additional evidence <span className="text-gray-400 font-normal">(optional)</span></span>
                <svg
                  className={`w-4 h-4 text-gray-400 transition-transform ${showAdditionalEvidence ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showAdditionalEvidence && (
                <div className="px-4 pb-4 pt-1">
                  {/* File upload - using label for accessibility */}
                  <label
                    htmlFor="file-upload"
                    className={`block border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                      isUploading
                        ? 'border-gray-300 bg-gray-50 cursor-wait'
                        : 'border-gray-200 hover:border-gray-400 hover:bg-gray-50 cursor-pointer'
                    }`}
                  >
                    <input
                      id="file-upload"
                      type="file"
                      multiple
                      accept="image/*,video/*,application/pdf,.heic,.heif"
                      onChange={handleFileChange}
                      className="sr-only"
                      disabled={isUploading}
                      data-testid="submit-input-file-upload"
                    />
                    {isUploading ? (
                      <>
                        <Loader2 className="w-8 h-8 mx-auto text-gray-500 mb-2 animate-spin" />
                        <p className="text-sm font-medium text-gray-700">Uploading...</p>
                      </>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                        <p className="text-sm font-medium text-gray-700">Tap to upload</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Images, videos, or PDF (max {maxFiles})
                        </p>
                      </>
                    )}
                  </label>

                  {/* File list with progress */}
                  {uploads.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Files ({uploads.length}/{maxFiles})
                      </p>
                      {uploads.map((upload) => (
                        <div
                          key={upload.fileId}
                          className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg"
                        >
                          <div className="flex items-center gap-2.5 min-w-0 flex-1">
                            <div className="w-8 h-8 bg-white border border-gray-200 rounded flex items-center justify-center flex-shrink-0">
                              {upload.status === 'completed' ? (
                                <CheckCircle className="w-4 h-4 text-green-500" />
                              ) : upload.status === 'error' ? (
                                <AlertCircle className="w-4 h-4 text-red-500" />
                              ) : upload.status === 'uploading' ? (
                                <Loader2 className="w-4 h-4 text-gray-500 animate-spin" />
                              ) : (
                                <Camera className="w-4 h-4 text-gray-400" />
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-gray-900 truncate">{upload.fileName}</p>
                              {upload.status === 'uploading' && (
                                <div className="mt-1 w-full bg-gray-200 rounded-full h-1.5">
                                  <div
                                    className="bg-gray-600 h-1.5 rounded-full transition-all"
                                    style={{ width: `${upload.progress}%` }}
                                  />
                                </div>
                              )}
                              {upload.status === 'error' && (
                                <p className="text-xs text-red-600">{upload.error}</p>
                              )}
                              {upload.status === 'completed' && (
                                <p className="text-xs text-green-600">Ready</p>
                              )}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeUpload(upload.fileId)}
                            disabled={upload.status === 'uploading'}
                            className="p-1 text-gray-400 hover:text-red-600 rounded transition-colors disabled:opacity-50"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Summary Card */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Review your complaint</h3>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-gray-500">Restaurant</dt>
                  <dd className="font-medium text-gray-900 text-right">
                    {selectedStore?.storeName || '—'}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Distributor</dt>
                  <dd className="font-medium text-gray-900 text-right">
                    {selectedSupplier?.name || '—'}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Category</dt>
                  <dd className="font-medium text-gray-900 text-right">
                    {categories.find((c) => c.id === formData.category)?.label || '—'}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Manufacturer</dt>
                  <dd className="font-medium text-gray-900 text-right truncate ml-4">
                    {manufacturers.find(m => m.manufacturerId === formData.manufacturer)?.name || formData.manufacturer || '—'}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Product</dt>
                  <dd className="font-medium text-gray-900 text-right truncate ml-4">
                    {formData.productName || '—'}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Delivery Date</dt>
                  <dd className="font-medium text-gray-900 text-right">
                    {formData.deliveryDate || '—'}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Batch Code</dt>
                  <dd className="font-medium text-gray-900 text-right">
                    {formData.batchCode || '—'}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Affected Units</dt>
                  <dd className="font-medium text-gray-900 text-right">
                    {formData.affectedUnits
                      ? `${formData.affectedUnits} ${formData.affectedUnitType === 'BAG' ? 'Bag(s)' : 'Case(s)'}`
                      : '—'}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Bag Picture</dt>
                  <dd className={`font-medium ${bagPicture ? 'text-green-600' : 'text-red-600'}`}>
                    {bagPicture ? 'Attached' : 'Required'}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Case Picture</dt>
                  <dd className={`font-medium ${casePicture ? 'text-green-600' : 'text-red-600'}`}>
                    {casePicture ? 'Attached' : 'Required'}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Attachments</dt>
                  <dd className="font-medium text-gray-900">
                    {completedMediaKeys.length} file{completedMediaKeys.length !== 1 ? 's' : ''}
                    {hasFailedUploads && (
                      <span className="text-red-600 ml-1">(some failed)</span>
                    )}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        )}

        {/* Sticky Navigation Buttons - positioned above mobile nav */}
        <div className="fixed bottom-[calc(4rem+env(safe-area-inset-bottom,0px))] left-0 right-0 lg:static lg:mt-6 bg-white lg:bg-transparent border-t lg:border-0 border-gray-200 p-4 lg:p-0 z-10">
          <div className="max-w-2xl mx-auto flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={currentStep === 1 ? () => navigate(ROUTES.COMPLAINTS) : handleBack}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
              data-testid="submit-btn-back"
            >
              {currentStep === 1 ? 'Cancel' : 'Back'}
            </button>

            {currentStep < totalSteps ? (
              <button
                type="button"
                onClick={handleNext}
                data-testid="submit-btn-continue"
                className="flex-1 lg:flex-none px-6 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
              >
                Continue
              </button>
            ) : hasFailedUploads ? (
              <button
                type="button"
                onClick={() => {
                  removeFailedUploads();
                  setErrors((prev) => ({ ...prev, submit: undefined }));
                }}
                data-testid="submit-btn-remove-failed"
                className="flex-1 lg:flex-none px-6 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
              >
                Remove Failed Uploads
              </button>
            ) : (
              <button
                type="submit"
                disabled={isSubmitting || isUploading}
                onClick={() => {
                  intentionalSubmitRef.current = true;
                }}
                data-testid="submit-btn-submit"
                className="flex-1 lg:flex-none px-6 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Submitting...
                  </>
                ) : isUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  'Submit Complaint'
                )}
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
